<template>
  <div>
    <label v-if="docLabel">
      {{ docLabel }}
    </label>
    <i
      v-if="tooltipMessage"
      :id="tooltipId"
      class="fas fa-question text-info ml-2"
      :class="tooltipClass"
      style="cursor: pointer"
      :style="tooltipStyle"
    />
    <BTooltip
      :content="tooltipMessage"
      :placement="tooltipPlacement"
      :tooltip-class="tooltipClass"
      :tooltip-style="tooltipStyle"
      :target="tooltipId"
    />

    <div v-if="disabled" class="example-avatar p-4 text-center">
      <img v-if="fileUrl" :src="fileUrl" class="avatar-upload-photo" />
      <div v-else>
        <p class="text-primary">No Current Upload</p>
      </div>
    </div>
    <div v-else class="example-avatar p-4">
      <div v-show="$refs.upload && $refs.upload.dropActive" :class="[noOverlay ? 'drop-active-no-overlay' : 'drop-active']">
        <h3>Drop files to upload</h3>
      </div>
      <div v-show="!edit" class="avatar-upload">
        <div class="text-left">
          <label :for="inputId" class="w-100 d-flex justify-content-center" style="height: 100px">
            <div v-if="files.length || fileUrl" class="w-100">
              <div class="w-100 row m-0">
                <button v-if="allowDelete" class="btn btn-danger btn-sm ml-auto" style="position: relative; z-index: 1" @click="removeImage">
                  X
                </button>
              </div>

              <img
                v-if="allowDelete"
                :src="files[0] ? files[0].url : fileUrl"
                class="avatar-upload-photo"
                :style="{
                  'margin-top': '-30px',
                  'z-index': 1,
                }"
              />
              <img v-if="!allowDelete" :src="files[0] ? files[0].url : fileUrl" class="avatar-upload-photo" />
            </div>
            <div v-else class="m-auto">
              <div v-if="$refs.upload && !$refs.upload.dropActive && noOverlay">
                <h4 class="pt-2 d-inline drop-title">Click or Drop Image</h4>
              </div>
              <div v-else>
                <h4 class="pt-2 d-inline">Drop Image Here</h4>
              </div>
              <i class="fas fa-cloud-upload-alt d-block pt-2" />
            </div>
          </label>
        </div>
        <div class="text-center p-2">
          <file-upload
            ref="upload"
            v-model="files"
            extensions="gif,jpg,jpeg,png"
            accept="image/png,image/gif,image/jpeg"
            :input-id="inputId"
            class="btn btn-primary"
            :drop="!edit"
            @input-filter="inputFilter"
            @input-file="inputFile"
          >
            Click or Drop Image
          </file-upload>
        </div>
      </div>
      <div v-show="files.length && edit" class="avatar-edit">
        <div v-if="files.length" class="avatar-edit-image">
          <div class="row text-center">
            <div class="col">
              <div style="max-width: 150px; width: 150px; max-height: 100px; margin: auto">
                <img ref="editImage" :src="files[0].url" style="max-width: 100%" />
              </div>
            </div>
            <div class="col m-auto pl-0">
              <div class="text-center m-auto">
                <button type="button" class="btn btn-secondary m-1" @click.prevent="$refs.upload.clear">Cancel</button>
                <button type="submit" class="btn btn-primary m-1" @click.prevent="editSave">Set</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="fileSizeLimit" class="text-sm mt-1">
      The maximum file size is: <strong>{{ filesize(fileSizeLimit) }}</strong>
    </div>
  </div>
</template>

<script>
import Cropper from "cropperjs";
import FileUpload from "vue-upload-component";
import { mapActions } from "vuex";
import { filesize } from "@/services/filters";

export default {
  name: "FreeImageCropper",
  components: {
    FileUpload,
  },
  props: {
    disabled: { type: Boolean, default: false },
    allowDelete: { type: Boolean, default: false },
    inputId: { type: String, default: "avatar" },
    fileUrl: { type: String },
    resetImage: { type: Boolean, required: false, default: false },
    noOverlay: { type: Boolean, default: false },
    docLabel: { type: String, required: false },
    tooltipMessage: { type: String },
    tooltipPlacement: { type: String, default: "bottom" },
    tooltipId: { type: String },
    tooltipClass: { type: String },
    tooltipStyle: { type: Object },
    fileSizeLimit: { type: Number, default: null },
  },
  emits: ["fileAdded", "deleteImage"],
  data() {
    return {
      files: [],
      edit: false,
      cropper: false,
      tooltipVisible: false,
    };
  },
  watch: {
    edit(value) {
      if (value) {
        this.$nextTick(function setCropper() {
          if (!this.$refs.editImage) {
            return;
          }
          const cropper = new Cropper(this.$refs.editImage, {
            // aspectRatio: 1 / 1,
            viewMode: 1,
            autoCropArea: 1,
            minContainerWidth: 100,
          });
          this.cropper = cropper;
        });
      } else if (this.cropper) {
        this.cropper.destroy();
        this.cropper = false;
      }
    },
  },
  methods: {
    filesize,
    ...mapActions({
      setSnackbar: "ui/setSnackbar",
    }),
    createFile(file, cb) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = function readResult() {
        cb(reader.result);
      };
      reader.onerror = function (error) {
        // There was an error reading doc
      };
    },
    editSave() {
      let validSize = true;
      this.edit = false;
      const oldFile = this.files[0];
      const binStr = atob(this.cropper.getCroppedCanvas().toDataURL(oldFile.type).split(",")[1]);
      const arr = new Uint8Array(binStr.length);
      for (let i = 0; i < binStr.length; i += 1) {
        arr[i] = binStr.charCodeAt(i);
      }
      const file = new File([arr], oldFile.name, { type: oldFile.type });
      this.$refs.upload.update(oldFile.id, {
        file,
        type: file.type,
        size: file.size,
        active: true,
      });
      this.createFile(file, (result) => {
        const upload = {
          file: result,
          name: file.name,
          type: file.type,
          size: file.size,
          active: true,
        };
        if (this.fileSizeLimit) {
          if (upload.size > this.fileSizeLimit) {
            this.setSnackbar({
              variant: "info",
              message: `Image file is too large (${this.fileSizeFormated(upload.size)}), The max file size is: ${this.fileSizeFormated(
                this.fileSizeLimit,
              )}, You can try and crop the image more or upload a smaller file.`,
            });
            validSize = false;
          }
        }
        if (validSize) {
          this.$emit("fileAdded", upload);
        }
      });
      if (this.resetImage) {
        this.files = [];
      }
    },
    inputFile(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        this.$nextTick(function () {
          this.edit = true;
        });
      }
      if (!newFile && oldFile) {
        this.edit = false;
      }
    },
    removeImage() {
      const data = {
        inputId: this.inputId,
        fileUrl: this.fileUrl,
        file: this.files && this.files[0],
      };
      this.$emit("deleteImage", data);
    },

    inputFilter(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        if (!/\.(gif|jpg|jpeg|png|webp)$/i.test(newFile.name)) {
          this.setSnackbar({
            variant: "info",
            message: "Only Images are supported!",
          });
          return prevent();
        }
      }
      if (newFile && (!oldFile || newFile.file !== oldFile.file)) {
        newFile.url = "";
        const URL = window.URL || window.webkitURL;
        if (URL && URL.createObjectURL) {
          newFile.url = URL.createObjectURL(newFile.file);
        }
      }
    },

    fileSizeFormated(size) {
      return this.filesize(size);
    },
  },
};
</script>

<style scoped>
.rounded .example-avatar {
  border-radius: 0.25rem !important;
}
:deep(label) {
  /* border: 2px dashed grey; */
  text-align: center;
}
:deep(.avatar-upload div:nth-child(2)) {
  display: none;
}
:deep(.cropper-point.point-se) {
  bottom: -3px;
  cursor: nwse-resize;
  height: 5px;
  opacity: 1;
  right: -3px;
  width: 5px;
}
</style>
<style scoped lang="scss">
@import "@sass/_variables.scss";
.avatar-upload-photo {
  /* max-width: 200px;
    max-height: 90px; */
  max-height: 100px;
  max-width: 100%;
}
.example-avatar {
  border: 2px dashed $grey;
  .avatar-edit-image {
    max-width: 100%;
  }
  .drop-active {
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    position: fixed;
    z-index: 9999;
    opacity: 0.6;
    text-align: center;
    background: $black;
    h3 {
      margin: -0.5em 0 0;
      position: absolute;
      top: 50%;
      left: 0;
      right: 0;
      -webkit-transform: translateY(-50%);
      -ms-transform: translateY(-50%);
      transform: translateY(-50%);
      font-size: 40px;
      color: $white;
      padding: 0;
    }
  }
  .drop-active-no-overlay {
    height: 100%;
    width: 100%;
    position: relative;
    z-index: 9999;
    opacity: 0.6;
    text-align: center;
    background: $black;
    h3 {
      display: none;
    }
  }
}
</style>
